import React, { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Phone, Mail, Building2 } from "lucide-react";
import DetailPage from "@/pages/DetailPage";
import Splash from "@/pages/Splash";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => (
  <footer className="w-full text-center text-xs text-white/80 py-8" data-testid="global-footer">
    © 2025 All Rights Reserved | 713 Consulting & Development | Keith Bolden
  </footer>
);

const LogoTopRight = () => (
  <img src="/assets/digifit_logo.png" alt="DigiFit logo" className="fixed right-6 top-6 w-16 h-16 object-contain z-50" data-testid="logo-top-right"/>
);

const AccessGate = () => {
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const isValid = useMemo(() => code === "504went713", [code]);
  const openMail = (e) => {
    e.preventDefault();
    try { window.location.href = "mailto:713Consulting@gmail.com"; } catch {}
    setTimeout(() => {
      window.open('https://mail.google.com/mail/?view=cm&to=713Consulting@gmail.com', '_blank');
      window.open('https://mail.ionos.com/', '_blank');
    }, 300);
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{animation:'ani_gradient 21s ease infinite', background:'linear-gradient(-60deg, #F505D5 15%, #F507F5 19%, #2525FA 54%, #42F2FF 85%)', backgroundSize:'200% 200%'}}>
      <style>{`@keyframes ani_gradient{0%{background-position:0% 0%}50%{background-position:100% 100%}100%{background-position:0% 0%}}`}</style>
      <LogoTopRight />
      <div className="flex-1 grid place-items-center px-4">
        <Card className="w-full max-w-[19rem] bg-[#0b2a6b] shadow-2xl border border-white/15" data-testid="access-card">
          <CardHeader>
            <CardTitle className="text-3xl font-extrabold text-center bg-clip-text text-transparent bg-gradient-to-r from-[#F505D5] via-[#42F2FF] to-[#F66809]" data-testid="permission-title">Permission Required</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Input id="access" type="password" data-testid="access-input" placeholder="Enter Access Code" value={code} onChange={e=>setCode(e.target.value)} className="bg-black/60 text-white text-center placeholder:text-center border-white/60"/>
            </div>
            <Button data-testid="submit-access-button" className="w-full text-white font-semibold rounded-full px-6 py-6 border border-white/60" style={{background:'linear-gradient(-60deg, #F505D5 15%, #F507F5 19%, #2525FA 54%, #42F2FF 85%)', backgroundSize:'200% 200%', animation:'ani_gradient 21s ease infinite'}} onClick={()=> isValid ? navigate('/nda') : null}>Submit Access Code</Button>
            <Separator className="bg-white/10" />
            <p className="text-sm text-white/80" data-testid="contact-line">
              To view the DigiFit™ private beta webapp <a className="underline" href="mailto:713Consulting@gmail.com" onClick={openMail}>click here</a> to contact 713 Consulting & Development for a DigiFit.live Access Code
            </p>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

const NDANotice = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", zip_code: "" });
  const onSubmit = async () => {
    try {
      const res = await axios.post(`${API}/nda/acknowledgements`, form);
      if (res?.data?.id) navigate('/app');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative" style={{animation:'ani_gradient 21s ease infinite', background:'linear-gradient(-60deg, #F505D5 15%, #F507F5 19%, #2525FA 54%, #42F2FF 85%)', backgroundSize:'200% 200%'}}>
      <style>{`@keyframes ani_gradient{0%{background-position:0% 0%}50%{background-position:100% 100%}100%{background-position:0% 0%}}`}</style>
      <LogoTopRight />
      <div className="flex-1 max-w-6xl mx-auto px-4 py-10">
        <div className="mt-8 md:flex md:gap-8 md:items-start">
          <Card className="bg-black/60 backdrop-blur-xl border-white/10 md:basis-[60%]" data-testid="nda-card">
            <CardHeader>
              <div className="text-white text-3xl sm:text-4xl font-bold">NDA / NC NOTICE</div>
              <div className="text-white/90 text-xl">Confidentiality & Non-Compete</div>
            </CardHeader>
            <CardContent className="text-white/90 space-y-4 leading-relaxed text-sm">
              <p>By viewing or accessing these materials, you acknowledge that all content is the confidential and proprietary property of Keith Bolden and/or 713 Consulting and Development. You agree not to disclose, copy, share, or use these materials without prior written consent from the Owner. Any use, directly or indirectly, to compete with or harm the Owner's business is strictly prohibited.</p>
              <p>Violations may result in immediate injunctive relief, monetary damages (including actual losses, unjust enrichment, and exemplary damages for willful misconduct), attorneys' fees, and potential criminal prosecution under applicable federal and state law. These laws include the Defend Trade Secrets Act (18 U.S.C. §1836), the Economic Espionage Act (18 U.S.C. §§1831–1832), the Computer Fraud and Abuse Act (18 U.S.C. §1030), and the Texas Uniform Trade Secrets Act (Chapter 134A, Civil Practice & Remedies Code). Courts have upheld enforcement of trade-secret rights in cases such as Computer Mgmt. Assistance Co. v. Robert F. DeCastro, Inc., 220 F.3d 396 (5th Cir. 2000).</p>
              <p>By completing the acknowledgement below, you agree to be bound by these terms and confirm that you understand the legal consequences of unauthorized disclosure or competitive use. The Owner reserves all rights to seek remedies at law or equity without limitation.</p>
            </CardContent>
          </Card>
          <Card className="bg-black/60 backdrop-blur-xl border-white/10 md:basis-[30%] mt-8 md:mt-0">
            <CardHeader>
              <CardTitle className="text-white text-xl">Acknowledgement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Input id="full_name" data-testid="nda-full-name" placeholder="Full Name" className="bg-black/60 text-white" value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Input id="email" data-testid="nda-email" type="email" placeholder="Email Address" className="bg-black/60 text-white" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Input id="zip" data-testid="nda-zip" placeholder="ZIP Code" className="bg-black/60 text-white" value={form.zip_code} onChange={e=>setForm({...form, zip_code: e.target.value})} />
              </div>
              <Button data-testid="nda-submit" className="w-full rounded-full bg-gradient-to-r from-[#F505D5] via-[#42F2FF] to-[#F66809] text-white py-6 hover:opacity-90" onClick={onSubmit}>I Acknowledge & Agree</Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const Section = ({ id, title, to, children }) => (
  <Link to={to} className="block rounded-3xl p-8 bg-white/5 border border-white/10 hover:bg-white/10" data-testid={`${id}-section`}>
    <h2 className="text-white text-2xl mb-3 font-semibold">{title}</h2>
    <p className="text-white/80 text-sm leading-relaxed">{children}</p>
  </Link>
);

const MainApp = () => {
  useEffect(()=>{ axios.get(`${API}/`).catch(()=>{}); },[]);
  const openMail = (e) => {
    e.preventDefault();
    try { window.location.href = "mailto:713Consulting@gmail.com"; } catch {}
    setTimeout(() => {
      window.open('https://mail.google.com/mail/?view=cm&to=713Consulting@gmail.com', '_blank');
      window.open('https://mail.ionos.com/', '_blank');
    }, 400);
  };
  return (
    <div className="min-h-screen" style={{animation:'ani_gradient 21s ease infinite', background:'linear-gradient(-60deg, #F505D5 15%, #F507F5 19%, #2525FA 54%, #42F2FF 85%)', backgroundSize:'200% 200%'}}>
      <style>{`@keyframes ani_gradient{0%{background-position:0% 0%}50%{background-position:100% 100%}100%{background-position:0% 0%}}`}</style>
      <LogoTopRight />
      <div className="max-w-6xl mx-auto px-4 py-12 space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <Section id="app" title="DigiFit™ App" to="/detail/app">The Companion Mobile App — browse clothing from participating retailers, manage the virtual closet, and interact with the AI stylist.</Section>
          <Section id="website" title="Website" to="/detail/website">Investor-focused overview and growth roadmap.</Section>
          <Section id="mirror" title="DigiFit Mirror™" to="/detail/mirror">Smart mirror concept and in-store try-on.</Section>
          <Section id="ai" title="AI DigiFit Style Consultant™" to="/detail/ai">AI-driven style suggestions, fit predictions, and size guidance.</Section>
          <Section id="retail" title="DigiFit™ Retailer Network" to="/detail/retailer">Omnichannel partnerships and inventory sync.</Section>
          <Section id="invest" title="Why You Should Invest" to="/detail/invest">Reasons to invest and funding ask.</Section>
        </div>
        <div className="rounded-3xl p-8 bg-black/60 border border-white/10" data-testid="contact-support">
          <h3 className="text-white text-2xl mb-4">Get In Touch</h3>
          <p className="text-white/80">Ready to transform this vision into reality? Let's discuss this project and explore the possibilities.</p>
          <div className="grid sm:grid-cols-2 gap-8 mt-6 text-white/90 text-sm">
            <div className="space-y-2">
              <div className="font-semibold">Contact</div>
              <div className="flex items-center gap-2"><Phone size={16}/> <span>713.328.9736</span></div>
              <div className="flex items-center gap-2"><Mail size={16}/> <a className="underline" href="mailto:713Consulting@gmail.com" onClick={openMail}>713Consulting@gmail.com</a></div>
              <div className="flex items-start gap-2"><Building2 size={16}/> 
                <div>
                  <div>713 Consulting & Development</div>
                  <div>1942 W. Gray St., Box #1259</div>
                  <div>Houston, TX 77019</div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="font-semibold">Support Our Mission</div>
              <div>Zelle:  <span>713Consulting@gmail.com</span></div>
              <div>CashApp:  <span>$OriginalPower</span></div>
              <div>Venmo:  <span>@KeithBolden</span></div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Splash />} />
          <Route path="/access" element={<AccessGate />} />
          <Route path="/nda" element={<NDANotice />} />
          <Route path="/app" element={<MainApp />} />
          <Route path="/detail/:slug" element={<DetailPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors />
    </div>
  );
}

export default App;
